"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { mergeFavoriteSets, favoritesMergedKey, shouldAttemptMerge, withoutDropped } from "./favoriteSets"

const STORAGE_KEY = "redy_order_favorites"

function readStoredFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    // ignore corrupt/unavailable storage — favorites just stay empty
    return []
  }
}

function writeStoredFavorites(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // ignore write failures (private mode, quota) — UI still reflects state
  }
}

/** I2 (revisión): marca durable de que YA se intentó subir los favoritos de
 *  este navegador a esta cuenta. A diferencia de `mergedOnce` (un useRef, que
 *  renace en cada montaje), esta sobrevive a la próxima visita — sin ella, un
 *  lote fallido se reintentaba en cada carga de /order para siempre. */
function readMergedMark(accountId: string): boolean {
  try {
    return localStorage.getItem(favoritesMergedKey(accountId)) === "1"
  } catch {
    return false
  }
}

function writeMergedMark(accountId: string): void {
  try {
    localStorage.setItem(favoritesMergedKey(accountId), "1")
  } catch {
    // si falla el write, se reintenta la próxima visita — mismo costo que hoy
  }
}

/** Resultado que resuelve `onMerge` (el server action `mergeFavorites`).
 *  `ok:false` cubre tanto un fallo transitorio como "nada que reportar": en
 *  ningún caso se escribe la marca durable, así que se reintenta después. */
type MergeOutcome = { ok: boolean; dropped?: string[] }

type UseFavoritesOptions = {
  /** Favoritos de la cuenta, leídos en el servidor; undefined = sin sesión. */
  initial?: string[]
  /** uuid de la cuenta con sesión (no es dato sensible) — ancla la marca
   *  durable de merge por cuenta en localStorage. undefined = sin sesión. */
  accountId?: string
  /** Con sesión: escribe en la cuenta además del localStorage. */
  onToggle?: (id: string, next: boolean) => void
  /** Con sesión: sube UNA vez los del navegador que la cuenta no tenía, y
   *  reporta qué se pudo subir y qué no (ver MergeOutcome). */
  onMerge?: (ids: string[]) => Promise<MergeOutcome>
}

/**
 * Favorite restaurants persisted in localStorage (no account needed) and,
 * with a diner session, synced to the account (spec §4.3: `initial` ∪
 * localStorage, with the browser-only ids merged up once via `onMerge`).
 * `loaded` is false until the first client read so SSR/first paint can avoid
 * a hydration flash (we render nothing favorited until localStorage is read).
 */
export function useFavorites(opts: UseFavoritesOptions = {}) {
  const { initial, accountId, onToggle, onMerge } = opts
  // I1 (revisión): sembrado con `initial` — sin esto, el primer render (el
  // HTML del servidor Y el primer paint del cliente antes del efecto)
  // pintaba TODOS los corazones vacíos aunque el servidor ya supiera cuáles
  // son favoritos, y el efecto los "encendía" un instante después (parpadeo
  // en cada carga con sesión). Sin sesión, `initial` es undefined y el
  // resultado es el de siempre: vacío hasta leer localStorage.
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set(initial ?? []))
  const [loaded, setLoaded] = useState(false)
  const mergedOnce = useRef(false)

  useEffect(() => {
    // Ambas llamadas de abajo (setFavorites/setLoaded) son INCONDICIONALES —
    // a propósito: react-hooks/set-state-in-effect (pre-existente en este
    // archivo) marca un setState que solo corre bajo una rama (el `if (raw)`
    // que tenía esta lectura); un setState que siempre corre al montar no cae
    // en ese aviso. El resto (persistir, avisar el merge) sigue condicionado,
    // pero eso no son setState de React — son I/O y un callback del caller.
    const local = readStoredFavorites()
    const { union, missingInAccount } = mergeFavoriteSets(local, initial)
    setFavorites(new Set(union))
    setLoaded(true)
    if (initial === undefined) return
    writeStoredFavorites(union)

    // I2 (revisión): antes, el único guard era un useRef ("mergedOnce") que
    // renace en cada montaje — un lote que falló una vez (ok:false) se
    // reintentaba en CADA visita a /order, para siempre, bloqueando la
    // sincronización del resto. La marca durable (localStorage, por cuenta)
    // reemplaza esa función: si ya está puesta, ni se llama a onMerge.
    const alreadyMerged = accountId ? readMergedMark(accountId) : false
    if (!onMerge || mergedOnce.current || !shouldAttemptMerge({ missingInAccount, alreadyMerged })) {
      return
    }
    mergedOnce.current = true
    let cancelled = false
    onMerge(missingInAccount)
      .then((result) => {
        if (cancelled || !result?.ok) return
        // ok:false (fallo transitorio, p. ej. de red): NO se marca — se
        // reintenta en la próxima visita, que es lo correcto (no es un lote
        // inválido, es una falla momentánea).
        if (accountId) writeMergedMark(accountId)
        const dropped = result.dropped ?? []
        if (dropped.length === 0) return
        // Ids que el servidor reportó como inexistentes (location_id ya
        // borrado): se sacan de local para no reintentarlos en cada visita.
        setFavorites((prev) => {
          const next = new Set(withoutDropped([...prev], dropped))
          writeStoredFavorites([...next])
          return next
        })
      })
      .catch(() => {
        // fail-soft: igual que ok:false — sin marca, se reintenta después.
      })
    return () => {
      cancelled = true
    }
    // Solo al montar: `initial` es el favoritos-del-servidor del primer render
    // y no cambia en la vida de la página; volver a correr esto en cada
    // cambio de referencia de onMerge/onToggle duplicaría el merge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = useCallback(
    (id: string) => {
      setFavorites((prev) => {
        const next = new Set(prev)
        const nextFavorite = !next.has(id)
        if (nextFavorite) next.add(id)
        else next.delete(id)
        writeStoredFavorites([...next])
        onToggle?.(id, nextFavorite)
        return next
      })
    },
    [onToggle],
  )

  return { favorites, toggle, loaded }
}
